import type { ArticleCategoryResponse } from '@shared/api-utils';
import { getDateNumber, ROUTES } from '@shared/utils';
import { useTimeZone, useTranslations } from 'next-intl';
import NextLink from 'next/link';
import { BackChevron } from '../../BackChevron/BackChevron';
import { RichContent } from '../../RichContent/RichContent';
import { buildMagazinePageHref } from '../helpers/buildMagazinePageHref';

type ArticleHeadingProps = {
  title: string;
  summary: string;
  publishedDate: string;
  category?: ArticleCategoryResponse;
  className?: string;
};

function ArticleHeading({ title, summary, publishedDate, category, className }: ArticleHeadingProps) {
  const timeZone = useTimeZone();
  const t = useTranslations('magazine.article');
  const date = new Date(publishedDate);
  // `published_date` renders the locale-aware weekday/month/year itself via ICU date skeletons
  // (e.g. "Thursday 2nd July 2026"); `day` is only needed to pick the ordinal suffix, extracted
  // via `getDateNumber` rather than parsed out of a formatted string so it stays correct for
  // locales/calendars that don't use plain ASCII digit day numbers.
  const formattedDate = t('published_date', { date, day: getDateNumber(date, timeZone) });

  return (
    <div className={className} data-testid="article-heading">
      <BackChevron fallbackUrl={ROUTES.MAGAZINE.HOME} className="mb-4" />
      <p className="flex flex-row items-center gap-2 paragraph-s text-foreground" data-testid="article-heading-meta">
        {category && (
          <>
            <NextLink
              href={buildMagazinePageHref({ type: 'category', categorySlug: category.slug }, 1)}
              className="hover:underline"
              data-track-name="Category"
              data-testid="article-heading-category"
            >
              {category.title}
            </NextLink>
            <span aria-hidden="true"> • </span>
          </>
        )}
        <time dateTime={publishedDate} data-testid="article-heading-published-date">
          {formattedDate}
        </time>
      </p>
      <h1 className="heading-4 md:heading-3 text-foreground mt-2">{title}</h1>
      {/* Reading time is part of the rendered summary HTML — no separate styling per design decision. */}
      <RichContent information={summary} tag="div" className="mt-4 text-foreground [&_p]:!paragraph-l [&>:first-child]:mt-0" data-testid="article-heading-summary" />
    </div>
  );
}

export { ArticleHeading, type ArticleHeadingProps };
